import { CSSProperties, useEffect, useRef, useState } from 'react';

/**
 * Creates styles for a collapsible animation
 * @param isOpen - Whether the element is open or closed
 * @param maxHeight - The maximum height of the element when open (default: '150px')
 * @param additionalStyles - Additional styles to apply to the element
 * @returns CSS properties for the animation
 */
export const getCollapsibleAnimationStyles = (
  isOpen: boolean,
  maxHeight: string = '150px',
  additionalStyles?: CSSProperties,
): CSSProperties => {
  return {
    maxHeight: isOpen ? maxHeight : '0',
    opacity: isOpen ? 1 : 0,
    overflow: 'hidden',
    paddingTop: isOpen ? '10px' : '0',
    transition: `max-height 0.3s ${isOpen ? 'ease-in-out' : 'ease-out'}, opacity 0.3s ${isOpen ? 'ease-in-out' : 'ease-out'}, padding-top 0.3s ${isOpen ? 'ease-in-out' : 'ease-out'}, margin-top 0.3s ${isOpen ? 'ease-in-out' : 'ease-out'}`,
    maxWidth: '100%',
    ...additionalStyles,
  };
};

/**
 * Creates styles for a smooth transition between two icons based on a state
 * @param isActive - Whether to show the active icon or the inactive icon
 * @param transitionDuration - The duration of the transition in seconds (default: 0.3)
 * @returns CSS properties for the icon container and both active/inactive states
 */
export const getIconTransitionStyles = (
  isActive: boolean,
  transitionDuration: number = 0.3,
): {
  containerStyle: CSSProperties;
  activeIconStyle: CSSProperties;
  inactiveIconStyle: CSSProperties;
} => {
  return {
    containerStyle: {
      position: 'relative',
      width: '16px',
      height: '16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    activeIconStyle: {
      position: 'absolute',
      transition: `opacity ${transitionDuration}s ${isActive ? 'ease-in-out' : 'ease-out'}, transform ${transitionDuration}s ${isActive ? 'ease-in-out' : 'ease-out'}`,
      opacity: isActive ? 1 : 0,
      transform: isActive ? 'rotate(0deg)' : 'rotate(-90deg)',
    },
    inactiveIconStyle: {
      position: 'absolute',
      transition: `opacity ${transitionDuration}s ${isActive ? 'ease-in-out' : 'ease-out'}, transform ${transitionDuration}s ${isActive ? 'ease-in-out' : 'ease-out'}`,
      opacity: isActive ? 0 : 1,
      transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)',
    },
  };
};

type CollapsibleStateStyles = {
  open?: CSSProperties;
  closed?: CSSProperties;
};

type UseCollapsibleAnimationStylesParams = {
  isOpen: boolean;
  maxHeight?: string;
  additionalStyles?: CollapsibleStateStyles;
  exitStyles?: CSSProperties;
};

export const useCollapsibleAnimationStyles = ({
  isOpen,
  maxHeight = '150px',
  additionalStyles,
  exitStyles,
}: UseCollapsibleAnimationStylesParams) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasClosed, setHasClosed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'max-height' && !isOpen) {
        setHasClosed(true);
      }
    };

    node.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      node.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setHasClosed(false);
    }
  }, [isOpen]);

  const stateStyles = isOpen ? additionalStyles?.open : additionalStyles?.closed;

  const styles: CSSProperties = {
    maxHeight: isOpen ? maxHeight : '0',
    opacity: isOpen ? 1 : 0,
    overflow: 'hidden',
    paddingTop: isOpen ? '10px' : '0',
    transition: `all 0.3s ${isOpen ? 'ease-in-out' : 'ease-out'}`,
    maxWidth: '100%',
    ...stateStyles,
    ...(hasClosed && exitStyles),
  };

  return { ref, styles };
};
